'use client';

import React from 'react';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { apiGetAllNews } from '@/services/newsHome/api';

/* Dữ liệu fallback theo từng danh mục (GIỮ NGUYÊN) */
const FALLBACK_CONTENTS: Record<string, any> = {
    "investment": {
        title: "GIỚI THIỆU",
        description: "Rovi Investment - Thiết kế và cung cấp giải pháp tài chính toàn diện để các doanh nghiệp tăng trưởng tối ưu",
        content: `
            <p>Vì chúng tôi đã ở đó, nên chúng tôi hành động.</p>
            <p><strong>Nhanh gọn để bắt kịp thời cơ. Minh bạch để tạo dựng tin cậy.</strong></p>
            <p>Và đơn giản hóa mọi thứ để bạn hoàn toàn tự do tập trung vào điều quan trọng nhất: <strong>kiến tạo đế chế kinh doanh của riêng mình.</strong></p>
        `,
        image: "/images/Investment/about.png"
    },
    "travel": {
        title: "CÂU CHUYỆN THÀNH LẬP",
        description: "Rovi Travel - Nhà tổ chức du lịch Chuyên nghiệp",
        content: `
            <p> Trực thuộc tập đoàn Rovi Holdings, Rovi Travel là nhà tổ chức du lịch Chuyên nghiệp. Đến với Rovi Travel, quý khách hàng hoàn toàn an tâm về những dịch vụ chuyên nghiệp và tận tâm mà công ty mang đến.</p>
            <p><strong>Rovi Travel thành lập vào năm 2004 bởi bà Nguyễn Thị Kim Tiếng và có tên gọi ban đầu là Tiếng Gia.</strong></p>
        `,
        image: "/images/travel/picture.png"
    },
    "hospitality": {
        title: "CÂU CHUYỆN THÀNH LẬP",
        description: "Rovi Hospitality - Tư vấn và quản lý khách sạn, nhà hàng",
        content: `
            <p>Rovi Hospitality là mảng kinh doanh trực thuộc Rovi Holdings, tập trung vào lĩnh vực tư vấn, vận hành và quản lý hệ thống khách sạn – nhà hàng theo chuẩn quốc tế.</p>
            <p>Chúng tôi hướng đến mục tiêu xây dựng chuẩn mực mới cho ngành lưu trú và ẩm thực tại Việt Nam.</p>
        `,
        image: "/images/hospitality/hospitality2.png"
    },
    "logistics": {
        title: "CÂU CHUYỆN THÀNH LẬP",
        description: "Rovi Logistics - Kết nối hệ sinh thái vận tải và công nghệ trên toàn quốc",
        content: `
            <p> Giải pháp logistics là một trong những mảng kinh doanh chiến lược của Rovi Holdings.</p>
            <p>Rovi Logistics hướng tới phát triển, mở rộng hệ sinh thái trên toàn quốc để cung cấp giải pháp & dịch vụ logistics toàn diện cho khách hàng, đối tác.</p>
        `,
        image: "/images/logistic/xetai2.png"
    }
};

interface AboutInvestmentSectionProps {
    companyId?: string | number;
}

const AboutInvestmentSection = ({ companyId }: AboutInvestmentSectionProps) => {

    const params = useParams();

    const finalId = companyId ? String(companyId) : (params.id as string);

    /* Gọi API lấy danh sách bài viết */
    const { data: newsData, isLoading } = useQuery({
        queryKey: ['about-investment', finalId],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: Number(finalId),
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        enabled: !!finalId && !isNaN(Number(finalId)),
        staleTime: 5 * 60 * 1000,
    });

    /* Xác định dữ liệu hiển thị cuối cùng */
    let finalData = null;
    let currentCategoryKey = "investment";

    if (newsData && Array.isArray(newsData) && newsData.length > 0) {
        const categoryName = newsData[0].newsType?.title || "";
        const foundKey = Object.keys(FALLBACK_CONTENTS).find(key =>
            categoryName.toLowerCase().includes(key.toLowerCase())
        );

        if (foundKey) {
            currentCategoryKey = foundKey;
        }

        const apiIntroItem = newsData.find((item: any) => item.seoDescription === 'Giới thiệu');

        if (apiIntroItem) {
            finalData = apiIntroItem;
        } else if (foundKey) {
            finalData = FALLBACK_CONTENTS[foundKey];
        }
    }

    /* Render mô tả với định dạng màu sắc */
    const renderDesciption = (description?: string | null) => {
        if (!description) return null;

        const separator = '-';
        const index = description.indexOf(separator);

        if (index === -1) {
            return <span className="text-[#333333] font-medium">{description}</span>;
        }

        const firstPart = description.substring(0, index);
        const secondPart = description.substring(index);

        return (
            <>
                <span className="text-[#006633] font-medium">{firstPart}</span>
                <span className="text-[#333333] font-medium">{secondPart}</span>
            </>
        );
    };

    /* Điều kiện ẩn component khi chưa có dữ liệu */
    if (isLoading || !finalData) {
        return null;
    }

    /* Xác định ảnh hiển thị */
    const displayImage = finalData.image || FALLBACK_CONTENTS[currentCategoryKey]?.image || "/images/Investment/about.png";

    return (
        <section className="w-full bg-white py-6 md:py-8 lg:py-12">
            <div className="max-w-[62rem] mx-auto px-6 md:px-8">

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-10 lg:gap-16 items-start">

                    {/* Cột nội dung tiêu đề */}
                    <div className="text-center lg:text-left">
                        <p
                            className="text-[#006633] text-[12px] md:text-[12px] font-light tracking-[0.2em] uppercase mb-2"
                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                        >
                            {finalData.title}
                        </p>

                        <h2
                            className="text-2xl md:text-3xl lg:text-2xl xl:text-3xl leading-tight"
                            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                        >
                            {renderDesciption(finalData.description)}
                        </h2>
                    </div>

                    {/* Cột nội dung chi tiết */}
                    <div
                        className="about prose w-full break-words whitespace-normal space-y-3 leading-snug pt-2 lg:max-w-lg text-[13px] md:text-sm text-[#333333]"
                        style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                        dangerouslySetInnerHTML={{ __html: finalData.content || '' }}
                    />
                </div>

                {/* Khu vực hiển thị hình ảnh */}
                <div className="mt-0 md:mt-8 lg:mt-12 relative w-full h-auto">
                    <img
                        src={displayImage}
                        alt={finalData.title}
                        className="w-full h-auto rounded-lg object-cover"
                        loading="lazy"
                    />
                </div>

            </div>
        </section>
    );
};

export default AboutInvestmentSection;